import { useState, useMemo } from 'react'
import { motion } from 'framer-motion'
import GalleryGrid     from '../components/gallery/GalleryGrid'
import GalleryFilter   from '../components/gallery/GalleryFilter'
import GalleryLightbox from '../components/gallery/GalleryLightbox'
import SectionTitle    from '../components/ui/SectionTitle'
import CTA             from '../components/home/CTA'
import { BalloonLayer } from '../components/ui/BalloonSVG'
import { useGallery }  from '../hooks/useGallery'

export default function Gallery() {
  const { data: gallery, isLoading } = useGallery()
  const [activeFilter, setActiveFilter]   = useState('All')
  const [lightboxIndex, setLightboxIndex] = useState(-1)

  // Filter items by event type
  const filtered = useMemo(() => {
    if (!gallery) return []
    if (activeFilter === 'All') return gallery
    return gallery.filter((g) =>
      g.gallery_event_type?.toLowerCase() === activeFilter.toLowerCase()
    )
  }, [gallery, activeFilter])

  // Build lightbox slides from filtered set
  const slides = filtered
    .filter((g) => g.image?.url)
    .map((g) => ({
      src:         g.image.url,
      alt:         g.gallery_caption || g.image.alt || '',
      description: g.gallery_event_type,
    }))

  return (
    <>
      {/* ── Hero ─────────────────────────────────────────────────────────── */}
      <section
        className="relative pt-32 pb-20 overflow-hidden"
        style={{ background: 'linear-gradient(135deg, #FF6BB5 0%, #7C3AFF 100%)' }}
      >
        <div className="absolute top-1/3 right-1/4 w-72 h-72 bg-white/10 rounded-full blur-3xl pointer-events-none" />
        <BalloonLayer opacity={0.28} />

        <div className="container-custom relative z-10 text-center">
          <motion.p
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
            className="inline-flex items-center gap-2 mb-5 px-5 py-2 rounded-full bg-white/20 border border-white/30 text-white text-sm font-body font-bold tracking-widest uppercase backdrop-blur-sm"
          >
            📸 Our Gallery
          </motion.p>

          <motion.h1
            initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="font-display text-5xl md:text-6xl text-white mb-6"
          >
            Magical Moments
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="font-body text-white/80 text-lg max-w-xl mx-auto"
          >
            Real photos from our amazing celebrations
          </motion.p>
        </div>
      </section>

      {/* ── Grid + filter ────────────────────────────────────────────────── */}
      <section className="section-padding bg-surface">
        <div className="container-custom">
          <SectionTitle
            eyebrow="Real Celebrations"
            title="Moments We're Proud Of"
            subtitle="Click any image to explore it in full."
          />

          <GalleryFilter active={activeFilter} onChange={setActiveFilter} />

          {isLoading ? (
            <div className="columns-2 md:columns-3 lg:columns-4 gap-4 space-y-4">
              {Array(12).fill(null).map((_, i) => (
                <div key={i} className="skeleton rounded-2xl aspect-square break-inside-avoid mb-4" />
              ))}
            </div>
          ) : filtered.length > 0 ? (
            <GalleryGrid items={filtered} onImageClick={setLightboxIndex} />
          ) : (
            <div className="text-center py-20">
              <p className="text-5xl mb-4">📸</p>
              <p className="font-body text-ink-muted">
                {activeFilter === 'All'
                  ? 'Gallery coming soon — check back after our next event!'
                  : `No ${activeFilter} photos yet. Try another category.`}
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Lightbox */}
      {slides.length > 0 && (
        <GalleryLightbox
          slides={slides}
          open={lightboxIndex >= 0}
          index={lightboxIndex}
          onClose={() => setLightboxIndex(-1)}
        />
      )}

      <CTA />
    </>
  )
}
